/**
 * Safe localStorage wrapper that handles iOS Safari private mode
 * and other scenarios where localStorage might throw exceptions
 */

class SafeLocalStorage {
  private isAvailable(): boolean {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      // Test if we can actually write to localStorage (fails in iOS private mode)
      const testKey = '__vitly_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (_e) {
      return false;
    }
  }

  getItem(key: string): string | null {
    try {
      if (!this.isAvailable()) {
        return null;
      }
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn(`[SafeLocalStorage] Failed to get item "${key}":`, error);
      return null;
    }
  }

  setItem(key: string, value: string): boolean {
    try {
      if (!this.isAvailable()) {
        return false;
      }
      window.localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn(`[SafeLocalStorage] Failed to set item "${key}":`, error);
      return false;
    }
  }

  removeItem(key: string): boolean {
    try {
      if (!this.isAvailable()) {
        return false;
      }
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`[SafeLocalStorage] Failed to remove item "${key}":`, error);
      return false;
    }
  }

  clear(): boolean {
    try {
      if (!this.isAvailable()) {
        return false;
      }
      window.localStorage.clear();
      return true;
    } catch (error) {
      console.warn('[SafeLocalStorage] Failed to clear storage:', error);
      return false;
    }
  }
}

export const safeLocalStorage = new SafeLocalStorage();
